import { IconAlert } from '@consta/icons/IconAlert'
import { IconDraggable } from '@consta/icons/IconDraggable'
import { IconSelect } from '@consta/icons/IconSelect'
import { IconSelectOpen } from '@consta/icons/IconSelectOpen'
import { IconTrash } from '@consta/icons/IconTrash'
import { Button } from '@consta/uikit/Button'
import { Text } from '@consta/uikit/Text'
import { withTooltip } from '@consta/uikit/withTooltip'
import { TreeInput } from '@core/components'
import { selectActiveEntityColor, useAppSelector } from '@core/core/store'
import type { BaseEntity } from '@core/core/types'
import cn from 'classnames'
import { preventDefault } from 'common/helpers'
import type { BaseObject, Nullable } from 'common/types'
import type { FC } from 'react'
import { useState } from 'react'

import type { SelectLayerItemProps } from './types'
import css from './VelocitySimulationRightMenu.css'

const IconWithTooltip = withTooltip({
  direction: 'downCenter',
  appearTimeout: 300,
})(IconAlert)

const alertTooltipContent = (globalAlert: string, alertLayerDescription: string) => (
  <Text size="xs" view="primary">
    <div>{globalAlert}</div>
    <div>{alertLayerDescription}</div>
  </Text>
)

const mapImportAllowedTypes: BaseObject[] = ['map']
const markerImportAllowedTypes: BaseObject[] = ['marker']

export const SelectLayerItem: FC<SelectLayerItemProps> = ({
  calculationStatus,
  layerInfo,
  index,
  addSelectItem,
  deleteSelectItem,
  clearSelectItemValue,
  dragStart,
  dragEnter,
  drop,
  onCollapseItem,
  onExpandItem,
  itemResults,
}) => {
  const [isCurrentlyDraggable, setIsCurrentlyDraggable] = useState(false)
  const onMouseLeaveMoveLayer = () => {
    setIsCurrentlyDraggable(false)
  }

  const activeEntityColor = useAppSelector(selectActiveEntityColor)

  const globalAlert = calculationStatus.note ?? ''
  const alertLayerDescription = itemResults?.note ?? ''

  const onChangeTreeInput = (
    value: Nullable<BaseEntity>,
    type: 'map' | 'marker',
    iconColor?: string,
  ) => {
    if (value) {
      addSelectItem(value, iconColor)
    } else {
      clearSelectItemValue(type)
    }
  }

  const collapsedItemClassName = cn(css.collapsedItem, {
    [css.collapsedItemPosition]: !layerInfo.isCollapsed,
  })

  const isMarkerAlertExist = itemResults?.status === 'ErrorMarker'
  const isGridAlertExist = itemResults?.status === 'ErrorGrid'
  const isCalculationStatusFailed = calculationStatus.status === 'Failed'

  const isLayerAlertExist = isMarkerAlertExist || isGridAlertExist || isCalculationStatusFailed

  const gridTreeInputStatus = isGridAlertExist ? 'alert' : undefined
  const markerTreeInputStatus = isMarkerAlertExist ? 'alert' : undefined

  const mapLayerValue = layerInfo.map?.value ?? null
  const markerLayerValue = layerInfo.marker?.value ?? null

  const activeAlertLayerClassName = cn(css.selectLayerItemItemBox, {
    [css.selectLayerItemItemBoxAlert]: isLayerAlertExist,
  })

  const ButtonMoveLayerToShow = (
    <Button
      onlyIcon={true}
      iconLeft={IconDraggable}
      size="xs"
      view="clear"
      onMouseLeave={onMouseLeaveMoveLayer}
      onMouseDown={() => {
        setIsCurrentlyDraggable(true)
      }}
    />
  )

  const ButtonRemoveLayerToShow = (
    <Button
      onlyIcon={true}
      iconLeft={IconTrash}
      size="xs"
      view="clear"
      onClick={deleteSelectItem}
    />
  )

  const IconLayerAlertsInfoToShow = (
    <IconWithTooltip
      tooltipProps={{
        tooltipContent: alertTooltipContent(globalAlert, alertLayerDescription),
      }}
      size="xs"
      view="alert"
      className={css.iconAlerts}
      role="tooltip"
    />
  )

  const onCollapseToggle = !layerInfo.isCollapsed ? onCollapseItem : onExpandItem
  const collapseIcon = !layerInfo.isCollapsed ? IconSelectOpen : IconSelect

  return (
    <div
      key={index}
      className={activeAlertLayerClassName}
      draggable={isCurrentlyDraggable}
      onDragStart={e => {
        dragStart(e, index)
      }}
      onDragEnter={e => {
        dragEnter(e, index)
      }}
      onDragEnd={drop}
      onDragOver={preventDefault}>
      <div className={collapsedItemClassName}>
        <div className={css.collapsedItemWrapper}>
          <Button
            iconLeft={collapseIcon}
            view="clear"
            size="xs"
            onlyIcon={true}
            onClick={onCollapseToggle}
          />
          <Text size="xs" lineHeight="2xs" view="ghost">
            Слой {index + 1}
          </Text>
          {!!isLayerAlertExist && IconLayerAlertsInfoToShow}
        </div>
        <div>
          {ButtonRemoveLayerToShow}
          {ButtonMoveLayerToShow}
        </div>
      </div>
      {!layerInfo.isCollapsed && (
        <>
          <div className={css.selectLayerItemLayerBorderTopContainer}>
            <div className={css.selectLayerItemLayerBorderContainer}>
              <Text size="xs" lineHeight="2xs" view="secondary" className={css.textPaddingBottom}>
                Граница
              </Text>
              <TreeInput
                value={mapLayerValue}
                allowedTypes={mapImportAllowedTypes}
                tooltip="Импортировать карту"
                color={layerInfo.map?.color}
                treeInputProps={{
                  placeholder: 'Карта изохрон',
                  status: gridTreeInputStatus,
                  className: css.treeInput,
                }}
                onChange={value => {
                  onChangeTreeInput(value, 'map', activeEntityColor)
                }}
              />
            </div>
            <div className={css.selectLayerItemLayerBorderContainer}>
              <Text size="xs" lineHeight="2xs" view="secondary" className={css.textPaddingBottom}>
                Маркер
              </Text>
              <TreeInput
                value={markerLayerValue}
                allowedTypes={markerImportAllowedTypes}
                tooltip="Импортировать маркер"
                color={layerInfo.marker?.color}
                treeInputProps={{
                  placeholder: 'Маркер',
                  status: markerTreeInputStatus,
                  className: css.treeInput,
                  role: 'treeitem',
                }}
                chooseEntityNameInsteadOfId={true}
                onChange={value => {
                  onChangeTreeInput(value, 'marker', activeEntityColor)
                }}
              />
            </div>
          </div>
          <div className={css.selectLayerItemLayerBorderTopContainer}>
            <div className={css.selectLayerItemLayerBorderContainer}>
              <div className={css.selectLayerItemLayerMarkerStats}>
                <Text size="xs" lineHeight="2xs" view="secondary" className={css.textPaddingRight}>
                  Vt<sub>min</sub>
                </Text>
                <Text size="xs" lineHeight="2xs" view="secondary">
                  {itemResults?.stats?.vMin ?? 0}
                </Text>
              </div>
              <div className={css.selectLayerItemLayerMarkerStats}>
                <Text size="xs" lineHeight="2xs" view="secondary" className={css.textPaddingRight}>
                  Vt<sub>max</sub>
                </Text>
                <div>
                  <Text size="xs" lineHeight="2xs" view="secondary" className={css.textPaddingTop}>
                    {itemResults?.stats?.vMax ?? 0}
                  </Text>
                </div>
              </div>
            </div>
            <div className={css.selectLayerItemLayerBorderContainer}>
              <div className={css.selectLayerItemLayerMarkerStats}>
                <Text size="xs" lineHeight="2xs" view="secondary" className={css.textPaddingRight}>
                  STD
                </Text>
                <Text size="xs" lineHeight="2xs" view="secondary">
                  {itemResults?.stats?.stdDeviation ?? 0}
                </Text>
              </div>
              <div className={css.selectLayerItemLayerMarkerStats}>
                <Text size="xs" lineHeight="2xs" view="secondary" className={css.textPaddingRight}>
                  R<sup>2</sup>
                </Text>
                <div>
                  <Text size="xs" lineHeight="2xs" view="secondary" className={css.textPaddingTop}>
                    {itemResults?.stats?.r2 ?? 0}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
